import { createModel } from '@rematch/core';
import type { RootModel } from '.';
import { IvehicleLocation } from '../type/vehicles-type';
import { IMyFleetTrack } from '../type/myfleet-track-type';

export interface ISelectedTrajectHistory {
	markerPositionState: {
		Current: { lat: number; lng: number };
		Departure: { lat: number; lng: number };
	};
	_id: string;
	RoadTripReformed: any[];
	vin: string;
	index: number;
	type: string;
	parkedPosition: { lat: number; lng: number };
	datetime: any[];
	temperature: any[];
	speed: any[];
	fuel_level: any[];
}

export interface IFilterTaskState {
	vin_filter: string;
	status_filter: string;
	sort: number;
	sortField: any;
}

const initialValue = {
	statusFilter: ['Stopped', 'Running', 'Disconnected', 'Idle', 'Parked', 'Trouble'],
	isFilterLoading: false,
	sortFilter: { sort: 1, sortfield: 'vin' },
	selectedTrajectHistory: [] as ISelectedTrajectHistory[],
	dashcamTrajectHistory: [] as any[],
	dashcamHistoryParams: null as { vin: string; startdate: string; enddate: string } | null,
	dashcamTrackingVin: null as string | null,   // ← ADD THIS LINE
	myFleetTrack: null as IMyFleetTrack | null,
	isHistoryPanelOpen: false, // ✅ controls normal HistoryDetailsCard right panel
	searchInputGeneral: '' as String,
	fleetSearched: {} as IvehicleLocation,
	filterTaskState: {
		vin_filter: 'All',
		status_filter: 'All',
		sort: 1,
		sortField: 'vin',
	} as IFilterTaskState,
	vehicleDetailSelected: [] as IvehicleLocation[],
	dataPOI: [],
	dataPOIStart: [],
	dataPOIEnd: [],
	geofencePointOfInterest: { lat: '', lng: '', zoomMap: '', centerMap: '' },
	startGeofencePointOfInterest: { lat: '', lng: '', zoomMap: '', centerMap: '' },
	endGeofencePointOfInterest: { lat: '', lng: '', zoomMap: '', centerMap: '' },
	startGeofencePointOfInterestIsOpen: false,
	endGeofencePointOfInterestIsOpen: false,
	raduisPointOfInterest: 500,
	geofencePointOfInterestIsOpen: false,
	poiFocus: null as null | { lat: number; lng: number; zoom: number; ts: number },
	geofencePointCenter: { lat: '', lng: '' },
	accordianIndex: 0,
};

export const appStoreNoPersist = createModel<RootModel>()({
	state: { ...initialValue },
	reducers: {
		setStatusFilter(state, res) {
			return { ...state, statusFilter: res };
		},
		setDataPOI(state, res) {
			return { ...state, dataPOI: res };
		},
		setSelectedTrajectHistory(state, res) {
			return { ...state, selectedTrajectHistory: res };
		},
		setDashcamTrajectHistory(state, res) {
			return { ...state, dashcamTrajectHistory: res };
		},
		setDashcamHistoryParams(state, res) {
			return { ...state, dashcamHistoryParams: res };
		},
		setDashcamTrackingVin(state, res: string | null) {  // ← ADD THIS REDUCER
			return { ...state, dashcamTrackingVin: res };
		},
		setMyFleetTrack(state, res: IMyFleetTrack | null) {
			return { ...state, myFleetTrack: res };
		},
		// ✅ NEW — controls normal HistoryDetailsCard right panel
		setHistoryPanelOpen(state, res: boolean) {
			return { ...state, isHistoryPanelOpen: res };
		},
		setInputSearchBar(state, res) {
			return { ...state, searchInputGeneral: res };
		},
		setFleetSearch(state, res) {
			return { ...state, fleetSearched: res };
		},
		setSortFilter(state, res) {
			return { ...state, sortFilter: res };
		},
		setFilterMaintenance(state, res) {
			return { ...state, filterTaskState: { ...state.filterTaskState, ...res } };
		},
		setVehicleSelectedToMap(state, res) {
			return { ...state, vehicleDetailSelected: res };
		},
		setIsFilterLoading(state, res) {
			return { ...state, isFilterLoading: res };
		},
		setGeofencePointOfInterest(state, res) {
			return { ...state, geofencePointOfInterest: res };
		},
		setGeofencePointOfInterestIsOpen(state, res) {
			return { ...state, geofencePointOfInterestIsOpen: res };
		},
		setPoiFocus(state, res) {
			return { ...state, poiFocus: res };
		},
		setRaduisPointOfInterest(state, res) {
			return { ...state, raduisPointOfInterest: res };
		},
		setGeofencePointCenter(state, res) {
			return { ...state, geofencePointCenter: res };
		},
		setAccordianIndex(state, res) {
			return { ...state, accordianIndex: res };
		},
		setDataPOIStart(state, res) {
			return { ...state, dataPOIStart: res };
		},
		setDataPOIEnd(state, res) {
			return { ...state, dataPOIEnd: res };
		},
		setStartGeofencePointOfInterest(state, res) {
			return { ...state, startGeofencePointOfInterest: res };
		},
		setEndGeofencePointOfInterest(state, res) {
			return { ...state, endGeofencePointOfInterest: res };
		},
		setStartGeofencePointOfInterestIsOpen(state, res) {
			return { ...state, startGeofencePointOfInterestIsOpen: res };
		},
		setEndGeofencePointOfInterestIsOpen(state, res) {
			return { ...state, endGeofencePointOfInterestIsOpen: res };
		},
		clearStore() {
			return { ...initialValue };
		},
	},

	effects: (dispatch) => ({
		changeStatusFilter(payload: string[]) {
			this.setStatusFilter(payload);
		},
		changeSelectedTrajectHistory(payload: any[]) {
			this.setSelectedTrajectHistory(payload);
		},
		changeDashcamTrajectHistory(payload: any[]) {
			this.setDashcamTrajectHistory(payload);
		},
		changeInputSearchBarMyFleet(payload: string) {
			this.setInputSearchBar(payload);
		},
		changeFleetDetailMap(payload) {
			this.setFleetSearch(payload);
		},
		changeSortFilter(payload) {
			this.setSortFilter(payload);
		},
		changeFilterMaintenance(payload: IFilterTaskState) {
			this.setFilterMaintenance(payload);
		},
		addVehicleSelectedToMap(payload) {
			this.setVehicleSelectedToMap(payload);
		},
		handleChangePointInterest(payload: any) {
			this.setGeofencePointOfInterest(payload);
		},
		handleStateMapPointInterest(payload: any, rootState) {
			this.setGeofencePointOfInterestIsOpen(payload);
		},
		handlePoiFocus(payload: any) {
			// ts makes every request unique so focusing the same POI twice still moves the map
			this.setPoiFocus(payload ? { zoom: 15, ...payload, ts: Date.now() } : null);
		},
		handleRaduisPointInterest(payload: any, rootState) {
			this.setRaduisPointOfInterest(payload);
		},
		handleCenterPointInterest(payload: any, rootState) {
			this.setGeofencePointCenter(payload);
		},
		handleStartPointInterest(payload: any) {
			this.setStartGeofencePointOfInterest(payload);
		},
		handleEndPointInterest(payload: any) {
			this.setEndGeofencePointOfInterest(payload);
		},
		handleStartMapPointInterest(payload: boolean) {
			this.setStartGeofencePointOfInterestIsOpen(payload);
		},
		handleEndMapPointInterest(payload: boolean) {
			this.setEndGeofencePointOfInterestIsOpen(payload);
		},
	}),
});